import { useMemo } from "react";
import {
  createFileRoute,
  type ErrorComponentProps,
  Navigate,
  Outlet,
  useMatchRoute,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { TravellersSelectionProvider } from "@/app/lib/context/TravellersSelectionContext";
import * as Sentry from "@sentry/react";
import { usePurchaseStateContext } from "@/app/lib/context/PurchaseStateContext";
import { BookingError } from "@/app/ui/errors/BookingError";
import clsx from "clsx";
import { WhatsAppButton } from "@/app/ui/WhatsAppButton";
import { useRouteConfig } from "@/app/lib/hooks/useRouteConfig";
import { isOfferIsNotAvailableAnymoreError } from "@/app/lib/assertions";
import { OfferNotAvailableError } from "@/app/ui/OfferNotAvailableError";
import { type OfferSelectedEventData } from "@/app/lib/types/types";
import { useHotelSearchDataContext } from "@/app/lib/context/HotelSearchDataContext";
import { getGroupedTravellersFromRooms } from "@/app/lib/utils/transformTraveler";
import { travellersQueryOptions } from "@/app/lib/hooks/useTravellers";

export const Route = createFileRoute("/_authenticated/_hotels/_hotel-offer")({
  component: RouteComponent,
  errorComponent: (props: ErrorComponentProps) => {
    Sentry.captureException(props.error);

    if (isOfferIsNotAvailableAnymoreError(props.error)) {
      return <OfferNotAvailableError variant="hotel" />;
    }

    return <BookingError {...props} type="hotels" />;
  },
  onEnter: () => {
    const event = new CustomEvent<OfferSelectedEventData>("offerselected", {
      detail: {
        offerType: "hotel",
      },
    });
    window.dispatchEvent(event);
  },
});

function RouteComponent() {
  const matches = useMatchRoute();
  const { purchaseState } = usePurchaseStateContext();
  const { searchData } = useHotelSearchDataContext();
  useQuery(travellersQueryOptions()); // for preloading

  const routeConfig = useRouteConfig();

  const isLastStepRoute = matches({
    from: "/",
    to: "/hotels/$id/confirmation",
  });

  const initialTravellersSlots = useMemo(() => {
    if (!searchData) return;

    const travellersToSelectByAge = getGroupedTravellersFromRooms(
      searchData.rooms,
    );

    return {
      ADT: Array.from({ length: travellersToSelectByAge.adults }, () => null),
      CHD: Array.from({ length: travellersToSelectByAge.children }, () => null),
      INF: Array.from({ length: travellersToSelectByAge.infants }, () => null),
    };
  }, [searchData]);

  if (!isLastStepRoute && purchaseState === "completed") {
    return <Navigate to="/" />;
  }

  return (
    <main
      className={clsx("no-footer main pt-4", {
        "with-drawer": !!routeConfig.drawer,
      })}
    >
      <TravellersSelectionProvider
        initialTravellersSlots={initialTravellersSlots}
        travelDate={searchData!.dates.checkIn}
      >
        <Outlet />
      </TravellersSelectionProvider>
      {!isLastStepRoute && <WhatsAppButton bottomPosition="20vh" />}
    </main>
  );
}
